import React, { useState, useEffect } from 'react';
import { Header, Modal, Button } from '../layout';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { setDocumentTitle } from '../utils/titleUtils';
import ClientsList from './clients/ClientsList';
import ClientForm from './clients/ClientForm';
import ClientOrdersList from './clients/ClientOrdersList';
import { OrderEditForm } from '../forms';
import { DocumentsSection } from '../features';

const ClientsManagement = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin;
  const canDelete = isAdmin;
  const canAdd = isAdmin;
  const canView = !isAdmin;
  const [agents, setAgents] = useState([]);
  const [viewOnly, setViewOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [clientsReloadKey, setClientsReloadKey] = useState(0);
  const [currentClient, setCurrentClient] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [dropdownClients, setDropdownClients] = useState([]);
  const [clientFormData, setClientFormData] = useState({
    CustSupp: '',
    CompanyName: '',
    TaxIdNumber: '',
    PriceList: '',
    Language: '',
    DiscountFormula: '',
    Salesperson: '',
    NameSalesPerson: '',
    AreaManager: '',
    NameAreaManager: '',
    CustomerClassification: '',
    Payment: '',
    Address: '',
    ZIPCode: '',
    City: '',
    County: '',
    Region: '',
    password: '',
    role: 'client',
    email: '',
    TELEFONO: '',
    MAIL: '',
    EMAIL_PERORDINE: '',
    EMAIL_PEROFFERTA01: '',
    EMAIL_PEROFFERTA02: '',
    is_active: 1
  });
  const [orderFormData, setOrderFormData] = useState({
    InternalOrdNo: '',
    ExternalOrdNo: '',
    OrderDate: '',
    ConfirmedDeliveryDate: '',
    Customer: '',
    OurReference: '',
    PriceList: '',
    Salesperson: '',
    AreaManager: '',
    Delivered: '0',
    Invoiced: '0',
    Cancelled: '0',
    TaxableAmount: '',
    TaxAmount: '',
    PayableAmount: ''
  });
  const [clientErrors, setClientErrors] = useState({});
  const [orderErrors, setOrderErrors] = useState({});

  useEffect(() => {
    setDocumentTitle('Gestione Clienti');
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch agents data
      const agentsResponse = await api.getAgents();
      if (agentsResponse.status === 'success' && agentsResponse.agents) {
        setAgents(agentsResponse.agents);
      } else {
        throw new Error('Invalid agents response format');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Impossibile caricare i dati agenti'
      });

      // Fallback to sample data for development
      // Mock agents data
      const mockAgents = [
        { Salesperson: 'AG001', Name: 'Mario Rossi', AreaManager: 'AM001', NameAreaManager: 'Giuseppe Verdi' },
        { Salesperson: 'AG002', Name: 'Laura Bianchi', AreaManager: 'AM001', NameAreaManager: 'Giuseppe Verdi' },
        { Salesperson: 'AM001', Name: 'Giuseppe Verdi', AreaManager: '', NameAreaManager: '' },
        { Salesperson: 'AM002', Name: 'Anna Gialli', AreaManager: '', NameAreaManager: '' }
      ];

      setAgents(mockAgents);
    } finally {
    }
  };

  const handleClientInputChange = (e) => {
    const { name, value } = e.target;

    // Create updated form data
    const updatedFormData = {
      ...clientFormData,
      [name]: value
    };

    // If the Salesperson field is changed, update related fields
    if (name === 'Salesperson') {
      const selectedAgent = agents.find(a => a.Salesperson === value);
      if (selectedAgent) {
        updatedFormData.NameSalesPerson = selectedAgent.Name || '';
        updatedFormData.AreaManager = selectedAgent.AreaManager || '';
        updatedFormData.NameAreaManager = selectedAgent.NameAreaManager || '';
      } else {
        updatedFormData.NameSalesPerson = '';
        updatedFormData.AreaManager = '';
        updatedFormData.NameAreaManager = '';
      }
    }

    setClientFormData(updatedFormData);

    // Clear error for this field
    if (clientErrors[name]) {
      setClientErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleOrderInputChange = (e) => {
    const { name, value } = e.target;
    setOrderFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (orderErrors[name]) {
      setOrderErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateClientForm = () => {
    const newErrors = {};

    if (!clientFormData.CustSupp) newErrors.CustSupp = 'Il codice cliente è obbligatorio';
    if (!clientFormData.CompanyName) newErrors.CompanyName = 'La ragione sociale è obbligatoria';
    if (!clientFormData.TaxIdNumber) newErrors.TaxIdNumber = 'La partita IVA è obbligatoria';
    if (!clientFormData.Salesperson) newErrors.Salesperson = 'L\'agente è obbligatorio';

    setClientErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOrderForm = () => {
    const newErrors = {};

    if (!orderFormData.InternalOrdNo) newErrors.InternalOrdNo = 'Il numero d\'ordine interno è obbligatorio';
    if (!orderFormData.OrderDate) newErrors.OrderDate = 'La data dell\'ordine è obbligatoria';
    if (!orderFormData.Customer) newErrors.Customer = 'Il cliente è obbligatorio';
    if (!orderFormData.Salesperson) newErrors.Salesperson = 'L\'agente è obbligatorio';

    // Validate amounts
    if (!orderFormData.TaxableAmount) newErrors.TaxableAmount = 'L\'imponibile è obbligatorio';
    else if (isNaN(orderFormData.TaxableAmount)) newErrors.TaxableAmount = 'L\'imponibile deve essere un numero';

    if (!orderFormData.TaxAmount) newErrors.TaxAmount = 'L\'IVA è obbligatoria';
    else if (isNaN(orderFormData.TaxAmount)) newErrors.TaxAmount = 'L\'IVA deve essere un numero';

    if (!orderFormData.PayableAmount) newErrors.PayableAmount = 'Il totale è obbligatorio';
    else if (isNaN(orderFormData.PayableAmount)) newErrors.PayableAmount = 'Il totale deve essere un numero';

    setOrderErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddClient = () => {
    setViewOnly(false);
    setCurrentClient(null);
    setClientFormData({
      CustSupp: '',
      CompanyName: '',
      TaxIdNumber: '',
      PriceList: 'PR',
      Language: 'IT',
      DiscountFormula: '',
      Salesperson: '',
      NameSalesPerson: '',
      AreaManager: '',
      NameAreaManager: '',
      CustomerClassification: '002',
      Payment: '',
      Address: '',
      ZIPCode: '',
      City: '',
      County: '',
      Region: '',
      password: '',
      hasPassword: false,
      role: 'client',
      email: '',
      TELEFONO: '',
      MAIL: '',
      EMAIL_PERORDINE: '',
      EMAIL_PEROFFERTA01: '',
      EMAIL_PEROFFERTA02: ''
    });
    setActiveTab('info');
    setModalOpen(true);
  };

  const handleViewClient = async (clientData) => {
    setViewOnly(true);
    await handleEditClient(clientData);
  };

  const handleEditClient = async (clientData) => {
    try {
      // If we only have basic data from DataTable, fetch full client details
      let client = clientData;

      if (!client || !client.CustSupp) {
        console.error('No client data provided for editing');
        return;
      }

      // If we don't have all required fields, fetch them from API
      if (!client.PriceList && !client.Language) {
        const response = await api.getClientById(client.CustSupp);
        if (response.status === 'success' && response.client) {
          client = response.client;
        } else {
          throw new Error('Failed to fetch complete client data');
        }
      }

      setCurrentClient(client);
      setClientFormData({
        CustSupp: client.CustSupp || '',
        CompanyName: client.CompanyName || '',
        TaxIdNumber: client.TaxIdNumber || '',
        PriceList: client.PriceList || '',
        Language: client.Language || '',
        DiscountFormula: client.DiscountFormula || '',
        Salesperson: client.Salesperson || '',
        NameSalesPerson: client.NameSalesPerson || '',
        AreaManager: client.AreaManager || '',
        NameAreaManager: client.NameAreaManager || '',
        CustomerClassification: client.CustomerClassification || '',
        Payment: client.Payment || '',
        Address: client.Address || '',
        ZIPCode: client.ZIPCode || '',
        City: client.City || '',
        County: client.County || '',
        Region: client.Region || '',
        password: '', // Don't show the actual password, just an empty field
        role: client.role || 'client',
        email: client.email || '',
        TELEFONO: client.TELEFONO || '',
        MAIL: client.MAIL || '',
        EMAIL_PERORDINE: client.EMAIL_PERORDINE || '',
        EMAIL_PEROFFERTA01: client.EMAIL_PEROFFERTA01 || '',
        EMAIL_PEROFFERTA02: client.EMAIL_PEROFFERTA02 || ''
      });
      setActiveTab('info');
      setModalOpen(true);

      // Fetch client orders when editing a client
      fetchClientOrders(client.CustSupp);
    } catch (error) {
      console.error('Error loading client for editing:', error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Impossibile caricare i dati del cliente'
      });
    }
  };

  const fetchClientOrders = async (clientId) => {
    setLoadingOrders(true);
    try {
      const response = await api.getOrders();
      if (response.status === 'success' && response.orders) {
        // Filter orders for this client and sort by date (newest first)
        const filteredOrders = response.orders
          .filter(order => order.Customer === clientId)
          .sort((a, b) => new Date(b.OrderDate) - new Date(a.OrderDate));

        setClientOrders(filteredOrders);
      } else {
        throw new Error('Invalid orders response format');
      }
    } catch (error) {
      console.error('Error fetching client orders:', error);
      // Fallback to empty array
      setClientOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchDropdownClients = async () => {
    try {
      const response = await api.getClientsDropdown();
      if (response.status === 'success' && response.clients) {
        setDropdownClients(response.clients);
      }
    } catch (error) {
      console.error('Error fetching dropdown clients:', error);
      // Use empty array as fallback
      setDropdownClients([]);
    }
  };

  const handleEditOrder = (order) => {
    // Close the client modal
    setModalOpen(false);

    // Set current order and prepare form data
    setCurrentOrder(order);
    setOrderFormData({
      InternalOrdNo: order.InternalOrdNo,
      ExternalOrdNo: order.ExternalOrdNo || '',
      OrderDate: order.OrderDate ? new Date(order.OrderDate).toISOString().split('T')[0] : '',
      ConfirmedDeliveryDate: order.ConfirmedDeliveryDate ? new Date(order.ConfirmedDeliveryDate).toISOString().split('T')[0] : '',
      Customer: order.Customer,
      OurReference: order.OurReference || '',
      PriceList: order.PriceList,
      Salesperson: order.Salesperson,
      AreaManager: order.AreaManager || '',
      Delivered: order.Delivered,
      Invoiced: order.Invoiced,
      Cancelled: order.Cancelled,
      TaxableAmount: order.TaxableAmount.toString(),
      TaxAmount: order.TaxAmount.toString(),
      PayableAmount: order.PayableAmount.toString()
    });

    // Fetch dropdown clients and open order modal
    fetchDropdownClients();
    setOrderModalOpen(true);
  };

  const handleDeleteClient = (client) => {
    Swal.fire({
      title: 'Sei sicuro?',
      text: `Vuoi eliminare il cliente ${client.CompanyName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sì, elimina!',
      cancelButtonText: 'Annulla'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Call API to delete client
          await api.deleteClient(client.CustSupp);

          // Show success message
          Swal.fire(
            'Eliminato!',
            'Il cliente è stato eliminato.',
            'success'
          );

          setClientsReloadKey(k => k + 1);
        } catch (error) {
          console.error('Error deleting client:', error);
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: error.message || 'Impossibile eliminare il cliente'
          });
        }
      }
    });
  };
  const handleSubmitClient = async (e) => {
    e.preventDefault();

    if (!validateClientForm()) return;

    try {
      // Create a copy of the form data to send to the API
      const clientDataToSend = { ...clientFormData };

      // Clean up any tracking properties that shouldn't be sent to API
      delete clientDataToSend.hasPassword;

      // Only include password in the update if it's been modified
      if (!clientDataToSend.password) {
        delete clientDataToSend.password;
      }

      if (currentClient) {
        // Update existing client
        await api.updateClient(currentClient.CustSupp, clientDataToSend);
      } else {
        // Add new client
        await api.createClient(clientDataToSend);
      }

      setModalOpen(false);

      Swal.fire({
        icon: 'success',
        title: currentClient ? 'Cliente aggiornato' : 'Cliente aggiunto',
        showConfirmButton: false,
        timer: 1500
      });

      setClientsReloadKey(k => k + 1);
    } catch (error) {
      console.error('Error saving client:', error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: error.message || 'Impossibile salvare il cliente'
      });
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    if (!validateOrderForm()) return;

    // Find the area manager for the selected salesperson
    const selectedAgent = agents.find(a => a.Salesperson === orderFormData.Salesperson);
    const areaManager = selectedAgent ? selectedAgent.AreaManager : '';

    const updatedOrderFormData = {
      ...orderFormData,
      AreaManager: areaManager,
      TaxableAmount: parseFloat(orderFormData.TaxableAmount),
      TaxAmount: parseFloat(orderFormData.TaxAmount),
      PayableAmount: parseFloat(orderFormData.PayableAmount)
    };

    try {
      // Update existing order
      await api.updateOrder(currentOrder.InternalOrdNo, updatedOrderFormData);

      // Update local state for client orders if needed
      if (currentClient && currentClient.CustSupp === updatedOrderFormData.Customer) {
        const updatedClientOrders = clientOrders.map(order =>
          order.InternalOrdNo === currentOrder.InternalOrdNo ? updatedOrderFormData : order
        );
        setClientOrders(updatedClientOrders);
      }

      setOrderModalOpen(false);

      Swal.fire({
        icon: 'success',
        title: 'Ordine aggiornato',
        showConfirmButton: false,
        timer: 1500
      });
    } catch (error) {
      console.error('Error saving order:', error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: error.message || 'Impossibile salvare l\'ordine'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title="Gestione Clienti"
        backText="Dashboard"
        backTo="/dashboard"
      />

      <ClientsList
        handleEditClient={(c) => { setViewOnly(false); handleEditClient(c); }}
        handleDeleteClient={handleDeleteClient}
        handleAddClient={handleAddClient}
        handleViewClient={handleViewClient}
        user={user}
        canEdit={canEdit}
        canDelete={canDelete}
        canAdd={canAdd}
        canView={canView}
        reloadKey={clientsReloadKey}
      />

      {/* Client Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setViewOnly(false); }}
        title={viewOnly ? "Dettagli Cliente" : (currentClient ? "Modifica Cliente" : "Nuovo Cliente")}
        size="full" // Changed from "xl" to "full" to make it larger
      >
        <div className="mb-4 border-b border-gray-200 -mx-4 sm:mx-0 px-4 sm:px-0 sticky top-0 bg-white z-10">
          <ul className="flex flex-nowrap overflow-x-auto -mb-px text-sm font-medium scrollbar-hide" role="tablist">
            <li className="shrink-0" role="presentation">
              <button
                className={`whitespace-nowrap px-4 py-3 sm:p-4 border-b-2 transition-colors ${activeTab === 'info' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                type="button"
                role="tab"
                onClick={() => setActiveTab('info')}
              >
                Informazioni
              </button>
            </li>
            {currentClient && (
              <li className="shrink-0" role="presentation">
                <button
                  className={`whitespace-nowrap px-4 py-3 sm:p-4 border-b-2 transition-colors ${activeTab === 'orders' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('orders')}
                >
                  Ordini
                </button>
              </li>
            )}
            {currentClient && (
              <li className="shrink-0" role="presentation">
                <button
                  className={`whitespace-nowrap px-4 py-3 sm:p-4 border-b-2 transition-colors ${activeTab === 'documents' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab('documents')}
                >
                  Documenti Riservati
                </button>
              </li>
            )}
          </ul>
        </div>

        {activeTab === 'info' && (
          <ClientForm
            formData={clientFormData}
            handleInputChange={handleClientInputChange}
            errors={clientErrors}
            agents={agents}
            currentClient={currentClient}
            handleSubmit={handleSubmitClient}
            onCancel={() => { setModalOpen(false); setViewOnly(false); }}
            readOnly={viewOnly}
          />
        )}
        {activeTab === 'orders' && (
          <ClientOrdersList
            clientOrders={clientOrders}
            loadingOrders={loadingOrders}
            currentClient={currentClient}
            handleEditOrder={handleEditOrder}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
          />
        )}
        {activeTab === 'documents' && currentClient && (
          <DocumentsSection
            ownerType="client"
            ownerId={currentClient.CustSupp}
            ownerName={currentClient.CompanyName}
            readOnly={!isAdmin}
          />
        )}
      </Modal>

      {/* Order Edit Modal */}
      <Modal
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        title={`Modifica Ordine ${currentOrder?.InternalOrdNo}`}
        size="full"
      >
        <OrderEditForm
          formData={orderFormData}
          handleInputChange={handleOrderInputChange}
          errors={orderErrors}
          clients={dropdownClients}
          agents={agents}
          currentOrder={currentOrder}
          handleSubmit={handleSubmitOrder}
          onCancel={() => setOrderModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default ClientsManagement;
